import { captureException, withScope } from '@sentry/browser'
import { omitBy } from 'es-toolkit'
import type { PaymentProvider } from 'src/services/helpers/payment'
import {
  getOnerwayPaymentData,
  getOnerwayPaymentMessage,
  getOnerwayPaymentStatus,
  getOnerwayPaymentStatusCode,
  getOnerwayResponseCode,
  isOnerwayJcbAeUnionPayRequired,
} from './onerwayResult'

type OnerwayCardProvider =
  | PaymentProvider.onerway
  | PaymentProvider.onerwayJcbAeUnionPay

export type OnerwayPaymentFailureSource =
  | 'card-group-mismatch'
  | 'payment-status-failed'
  | 'payment-response-error'
  | 'sdk-error'

type UnknownRecord = Record<string, unknown>

type OnerwayPaymentFailureSentryInput = {
  executeProvider: OnerwayCardProvider
  source: OnerwayPaymentFailureSource
  orderId?: string
  transactionId?: string
  response?: unknown
  error?: unknown
  fallbackRespCode?: string
  fallbackRespMsg?: string
}

export type OnerwayPaymentFailureSentryPayload = {
  message: string
  tags: Record<string, string>
  context: Record<string, unknown>
}

const isRecord = (value: unknown): value is UnknownRecord =>
  value !== null && typeof value === 'object'

const compactUndefinedValues = <T>(
  record: Record<string, T | undefined>,
): Record<string, T> =>
  omitBy(record, value => value === undefined) as Record<string, T>

const getErrorName = (error: unknown): string | undefined =>
  error instanceof Error ? error.name : undefined

const getErrorMessage = (error: unknown): string | undefined => {
  if (error instanceof Error) return error.message
  if (typeof error === 'string') return error
  return undefined
}

const getResponseDataKeys = (response: unknown): string[] | undefined => {
  const data = getOnerwayPaymentData(response)
  if (!isRecord(data)) return undefined

  return Object.keys(data)
    .filter(key => !/(card|pan|cvv|cvc|security|holder|expiry|exp)/i.test(key))
    .sort()
    .slice(0, 20)
}

export function buildOnerwayPaymentFailureSentryPayload({
  executeProvider,
  source,
  orderId,
  transactionId,
  response,
  error,
  fallbackRespCode,
  fallbackRespMsg,
}: OnerwayPaymentFailureSentryInput): OnerwayPaymentFailureSentryPayload {
  const respCode = getOnerwayResponseCode(response) ?? fallbackRespCode
  const statusCode =
    getOnerwayPaymentStatusCode(response) ?? fallbackRespCode ?? respCode
  const status = getOnerwayPaymentStatus(response)
  const message =
    getOnerwayPaymentMessage(response) ??
    fallbackRespMsg ??
    getErrorMessage(error)
  const requiresJcbAeUnionPay =
    source === 'card-group-mismatch' ||
    (response !== undefined && isOnerwayJcbAeUnionPayRequired(response))
  const issueCode = statusCode ?? respCode ?? source

  return {
    message: `Onerway payment failed: ${issueCode}`,
    tags: compactUndefinedValues({
      feature: 'payment',
      paymentProvider: 'onerway',
      provider: executeProvider,
      onerwayFailureSource: source,
      onerwayRespCode: respCode,
      onerwayStatusCode: statusCode,
      onerwayStatus: status,
      onerwayRequiresJcbAeUnionPay: requiresJcbAeUnionPay ? 'true' : 'false',
    }),
    context: compactUndefinedValues({
      source,
      provider: executeProvider,
      orderId,
      transactionId,
      respCode,
      statusCode,
      status,
      message,
      errorName: getErrorName(error),
      errorMessage: getErrorMessage(error),
      responseDataKeys: getResponseDataKeys(response),
    }),
  }
}

export function reportOnerwayPaymentFailureToSentry(
  input: OnerwayPaymentFailureSentryInput,
) {
  const payload = buildOnerwayPaymentFailureSentryPayload(input)

  withScope(scope => {
    Object.entries(payload.tags).forEach(([key, value]) => {
      scope.setTag(key, value)
    })
    scope.setContext('onerway_payment_failure', payload.context)

    const error = new Error(payload.message)
    error.name = 'OnerwayPaymentFailure'
    captureException(error)
  })
}
